import './Coverages.css';

import React, { useState } from 'react';

import {
  GetQuoteOk,
  UpdateCoverageInput
} from '../../../../generated/rating-quoting-graphql';
import EditPolicyPopup from '../EditPolicyPopup';
import { IneligibleAmtrustQuote } from './amtrustCoverage';
import { lobTypeToProductLabel } from './configs/productLabels';
import { CoverageDetail } from './Coverage';

interface CoveragesProps {
  coverageInput: GetQuoteOk;
  onUpdateCoverage: (coverage: UpdateCoverageInput) => void;
  /**
   * Return to the business info page so the answers can be changed. Async: the
   * quote page validates server-side that the group is still editable before
   * it navigates, so this may resolve without leaving the page.
   */
  onEdit: () => void | Promise<void>;
  applicationId: string;
  isEndorsement: boolean;
  lobType: string;
  /** Deselect this LOB via its primary coverage checkbox (NCP-4545). */
  onDeselectLob?: (lobType: string) => void;
  /** Whether a LOB may be removed — false when only one LOB is active. */
  canDeselectLob?: boolean;
  /**
   * Additional insured endorsement, rendered as the last row of this policy's
   * card. It sits outside .coverages-details so its address form is not
   * trapped in that list's scroll area.
   */
  additionalInsured?: React.ReactNode;
}

const Coverages: React.FC<CoveragesProps> = (props) => {
  const {
    coverageInput,
    onUpdateCoverage,
    applicationId,
    onEdit,
    isEndorsement,
    lobType,
    onDeselectLob,
    canDeselectLob,
    additionalInsured
  } = props;
  const { coveragesData, carrierPartner, region, quoteId } = coverageInput;

  // The LOB is "selected" when its defining coverage is chosen: CGL for GL, EO
  // for EO (the same pairing Coverage.tsx uses for the deselect checkbox). A
  // selected LOB is simply named, while an available-but-unselected one is
  // being offered, so its header becomes a question.
  const lobDefiningCoverageName = lobType === 'EO' ? 'eo' : 'cgl';
  const isLobSelected =
    coveragesData.find(
      (coverageData) => coverageData.name === lobDefiningCoverageName
    )?.chooseCoverage ?? true;
  const productLabel = lobTypeToProductLabel(lobType);

  // "Edit" leaves the quote page, so it is confirmed first.
  const [showEditPopup, setShowEditPopup] = useState(false);

  return (
    <div className="coverages-container mt-2 md:mt-0">
      <div className="coverages-header flex justify-between bg-secondary-light px-8 py-4 font-bold text-2xl text-quartary">
        <p>
          {isLobSelected
            ? `${productLabel} Policy:`
            : `Want to add a ${productLabel} policy?`}
        </p>
        <button
          className="text-base text-secondary underline"
          onClick={() => setShowEditPopup(true)}
          disabled={isEndorsement}
        >
          Edit
        </button>
      </div>
      <div className="coverages-inner-container px-8 pt-4 pb-8 relative md:mb-28 lg:mb-0">
        <div className="coverages-details overflow-hidden lg:overflow-auto max-h-full">
          {coveragesData
            .filter((coverageData) => coverageData.name !== 'additionalInsured') // additonal insured is in another component
            .map((coverageData) => {
              // Since we dont enable WC on quote page, we just show it as ineligible
              if (coverageData.name === 'workersCompensation') {
                return <IneligibleAmtrustQuote key={coverageData.name} />;
              }
              return (
                <CoverageDetail
                  coverageData={coverageData}
                  key={coverageData.name}
                  onUpdateCoverage={onUpdateCoverage}
                  region={region}
                  quoteId={quoteId}
                  applicationId={applicationId}
                  carrierPartner={carrierPartner}
                  lobType={lobType}
                  onDeselectLob={onDeselectLob}
                  canDeselectLob={canDeselectLob}
                />
              );
            })}
        </div>
        {additionalInsured}
      </div>
      <EditPolicyPopup
        displayModal={showEditPopup}
        handleClose={() => setShowEditPopup(false)}
        // Dismiss before handing off: onEdit validates the group server-side
        // and can decline to navigate, in which case the quote page shows its
        // "unable to modify" banner — which this modal would otherwise cover.
        handleProceed={() => {
          setShowEditPopup(false);
          void onEdit();
        }}
      />
    </div>
  );
};

export default Coverages;
